import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { api, priceFor, inr } from '@/lib/api';
import { useSiteSettings, buildWhatsAppUrl } from '@/hooks/useSiteSettings';
import { openWhatsAppWithTracking } from '@/lib/gtag';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Check, MessageCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function Checkout() {
    const { items, subtotal, clearCart } = useCart();
    const s = useSiteSettings();
    const nav = useNavigate();

    const [form, setForm] = useState({ customer_name: '', phone: '', email: '', address: '', pincode: '', notes: '' });
    const [refCode, setRefCode] = useState('');
    const [refInfo, setRefInfo] = useState(null);
    const [order, setOrder] = useState(null);
    const [busy, setBusy] = useState(false);

    const shipping = Number(s.shipping_flat || 1500);
    const discount = refInfo?.valid ? Math.round(subtotal * (refInfo.percent / 100)) : 0;
    const gst = Math.round(Math.max(0, subtotal - discount) * 0.18);
    const total = Math.max(0, subtotal - discount + gst + shipping);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const applyCode = async () => {
        if (!refCode.trim()) return;
        try {
            const r = await api.get('/codes/validate', { params: { code: refCode.trim().toUpperCase() } });
            setRefInfo(r.data);
            if (r.data.valid) toast.success(`Code applied — ${r.data.percent}% off`);
            else toast.error('Invalid code');
        } catch { toast.error('Could not validate code'); }
    };

    const submitOrder = async () => {
        if (!form.customer_name || !form.phone || !form.address || !form.pincode) {
            toast.error('Please fill all required fields'); return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                referral_code: refInfo?.valid ? refInfo.code : null,
                items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity, quality: i.quality })),
            };
            const r = await api.post('/orders', payload);
            setOrder(r.data.order);
            clearCart();
            toast.success('Order submitted — the Kanbr team will reach out shortly.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not place order');
        } finally { setBusy(false); }
    };

    const openWhatsAppFollowup = () => {
        if (!order) return;
        const lines = order.items.map((i) => `• ${i.quantity}× ${i.name} (${i.quality}) — ${inr(i.line_total)}`).join('\n');
        const msg = `Hi Kanbr, I've just placed order ${order.order_no}.\n${lines}\nTotal: ${inr(order.total)}. Please confirm.`;
        openWhatsAppWithTracking(buildWhatsAppUrl(s.whatsapp_number, msg), { source: 'checkout-confirm', message: msg });
    };

    if (items.length === 0 && !order) {
        return <div className="kanbr-container py-24 text-center text-slate-600">Your cart is empty. <Link to="/pre-crafted-modules" className="text-blue-900 underline">Browse modules</Link>.</div>;
    }

    if (order) {
        return (
            <div className="kanbr-container py-24 max-w-2xl mx-auto text-center" data-testid="order-success">
                <div className="w-20 h-20 bg-green-100 text-green-700 flex items-center justify-center mx-auto rounded-full"><Check className="w-8 h-8" /></div>
                <h1 className="font-display text-4xl font-bold text-slate-900 mt-6">Order Received!</h1>
                <p className="text-slate-600 mt-3">Order <strong>{order.order_no}</strong> · Total {inr(order.total)}.</p>
                <p className="text-sm text-slate-500 mt-4">Your full order + contact details have been sent to the Kanbr team on WhatsApp and email. We'll confirm within 24 hours.</p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button onClick={openWhatsAppFollowup} data-testid="co-followup-whatsapp" className="border border-green-500 text-green-700 py-3 px-6 hover:bg-green-50 flex items-center justify-center gap-2 text-sm font-medium">
                        <MessageCircle className="w-4 h-4" /> Chat with us on WhatsApp
                    </button>
                    <Link to="/" className="btn-primary rounded-none">Continue browsing</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="kanbr-container py-16">
            <div className="eyebrow mb-4">Checkout</div>
            <h1 className="font-display text-5xl font-bold text-slate-900 mb-14">Confirm your order.</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-8">
                    <section className="border border-slate-200 bg-white p-8" data-testid="checkout-form">
                        <h2 className="font-display text-2xl font-bold text-slate-900 mb-6">Shipping & Contact</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <Label>Full name *</Label>
                                <Input data-testid="co-name" value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} className="mt-1 rounded-none" />
                            </div>
                            <div>
                                <Label>Phone *</Label>
                                <Input data-testid="co-phone" value={form.phone} onChange={(e) => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} className="mt-1 rounded-none" placeholder="10-digit" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Email</Label>
                                <Input data-testid="co-email" value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-1 rounded-none" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Address *</Label>
                                <Textarea data-testid="co-address" value={form.address} onChange={(e) => set('address', e.target.value)} className="mt-1 rounded-none" rows={3} />
                            </div>
                            <div>
                                <Label>Pincode *</Label>
                                <Input data-testid="co-pincode" value={form.pincode} onChange={(e) => set('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} className="mt-1 rounded-none" />
                            </div>
                            <div>
                                <Label>Notes</Label>
                                <Input data-testid="co-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} className="mt-1 rounded-none" placeholder="Optional" />
                            </div>
                        </div>
                    </section>

                    <section className="border border-slate-200 bg-white p-8">
                        <h2 className="font-display text-2xl font-bold text-slate-900 mb-6">Referral / Discount code</h2>
                        <div className="flex gap-3">
                            <Input value={refCode} onChange={(e) => setRefCode(e.target.value.toUpperCase())} placeholder="Enter code (e.g. KANBR10)" data-testid="co-refcode" className="rounded-none" />
                            <Button onClick={applyCode} data-testid="co-apply-code" className="bg-slate-900 hover:bg-slate-800 rounded-none px-8">Apply</Button>
                        </div>
                        {refInfo?.valid && <div className="mt-3 text-sm text-green-700">{refInfo.type === 'referral' ? 'Referral' : 'Discount'} code applied · {refInfo.percent}% off</div>}
                    </section>
                </div>

                <aside className="border border-slate-200 bg-white p-8 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
                    <h2 className="font-display text-2xl font-bold text-slate-900 mb-6">Summary</h2>
                    <div className="space-y-2 text-sm text-slate-700">
                        <div className="flex justify-between"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
                        {discount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>- {inr(discount)}</span></div>}
                        <div className="flex justify-between"><span>GST (18%)</span><span data-testid="co-gst">{inr(gst)}</span></div>
                        <div className="flex justify-between"><span>Shipping</span><span>{inr(shipping)}</span></div>
                    </div>
                    <div className="border-t border-slate-200 my-4" />
                    <div className="flex justify-between text-lg font-display font-bold text-slate-900"><span>Total</span><span data-testid="co-total">{inr(total)}</span></div>

                    <Button onClick={submitOrder} disabled={busy} data-testid="co-place-order" className="w-full bg-blue-900 hover:bg-blue-950 rounded-none mt-6 py-6">
                        {busy ? 'Submitting…' : 'Submit order'}
                    </Button>
                    <p className="text-[11px] text-center text-slate-500 mt-2">Kanbr team will confirm your order and payment terms directly on WhatsApp within 24 hours.</p>

                    <div className="mt-6 text-xs text-slate-500 flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 text-slate-400 mt-0.5" />
                        <span>Your order and contact details are shared only with the Kanbr team. See <Link to="/policy" className="underline">Policy</Link>.</span>
                    </div>
                </aside>
            </div>
        </div>
    );
}
